import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getLabTestById } from "../../apis/LabOrder";
import { convertFromRaw,  Editor, EditorState, } from "draft-js";
import "draft-js/dist/Draft.css";

const ViewLabTest: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const testId = id ? parseInt(id) : 0;
  const [test, setTest] = useState<any>(null);
  const [isFetching, setIsFetching] = useState(true);

  useEffect(() => {
    if (testId) {
      fetchTestDetails(testId);
    }
  }, [testId]);

  const isDraftDescription = (value: string) => {
  try {
    const parsed = JSON.parse(value);
    return parsed?.blocks && Array.isArray(parsed.blocks);
  } catch {
    return false;
  }
};

  const getEditorStateFromRaw = (rawDescription?: string) => {
  if (!rawDescription) {
    return EditorState.createEmpty();
  }
  try {
    const content = convertFromRaw(JSON.parse(rawDescription));
    return EditorState.createWithContent(content);
  } catch (err) {
    console.error("Invalid Draft content", err);
    return EditorState.createEmpty();
  }
};

  const fetchTestDetails = async (id: number) => {
    try {
      setIsFetching(true);
      const res = await getLabTestById(id);
      setTest(res.data.data);
    } catch (err) {
      console.error("Error fetching test details:", err);
    } finally {
      setIsFetching(false);
    }
  };

  if (isFetching) {
    return (
      <div className="max-w-6xl p-6">
        <div className="text-center">Loading test details...</div>
      </div>
    );
  }

  if (!test) {
    return (
      <div className="max-w-6xl p-6">
        <div className="text-center text-red-600">Test not found</div>
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-6">
      <div className="mb-6">
        <h1 className="text-lg font-semibold text-gray-900">
          Test Details - {test.name || "-"}
        </h1>
      </div>

      {/* Test Details */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
        {/* Sections and Parameters - Single Table */}
        {test?.sections && test.sections.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full border border-gray-300">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-r border-gray-300">
                   
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-r border-gray-300">
                    Result
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-r border-gray-300">
                    Unit
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Reference Value
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {/* {test.sections.map((section: any, sectionIndex: number) => { */}
                {[...test.sections]
                  .sort(
                    (a: any, b: any) => Number(a.displayOrder) - Number(b.displayOrder)
                  )
                  .map((section: any, sectionIndex: number) => {

                  // const sectionParams = section.parameters || [];
                    const sectionParams = [...(section.parameters || [])].sort(
                      (a: any, b: any) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0)
                    );

                  if (sectionParams.length === 0) return null;

                  return (
                    <React.Fragment key={section.id || sectionIndex}>
                      {/* Section Header Row */}
                      <tr className="bg-gray-100">
                        <td
                          colSpan={4}
                          className="px-4 py-2 text-sm font-semibold text-gray-900 uppercase border-b border-gray-300"
                        >
                          {section.name}
                        </td>
                      </tr>
                      {/* Parameters Rows */}
                      {sectionParams.map((param: any, paramIndex: number) => {
                        // Format reference ranges to match the image format
                        const formatReferenceValue = () => {
                          if (param.referenceRanges && param.referenceRanges.length > 0) {
                            // Display all reference ranges in structured format
                            return param.referenceRanges.map((range: any, rangeIndex: number) => {
                              const rangeMin = range.rangeMin;
                              const rangeMax = range.rangeMax;
                              const categoryName = range.categoryName;
                              const ageMin = range.ageMin;
                              const ageMax = range.ageMax;
                              const gender = range.gender;
                              
                              // Check if both rangeMin and rangeMax are not available
                              const hasMin = rangeMin !== null && rangeMin !== undefined && rangeMin !== "";
                              const hasMax = rangeMax !== null && rangeMax !== undefined && rangeMax !== "";
                              
                              if (!hasMin && !hasMax) {
                                // If both are not available, show Absent
                                return (
                                  <div key={rangeIndex} className="text-sm text-gray-700">
                                    Absent
                                  </div>
                                );
                              }
                              
                              // Format range values with 2 decimal places
                              const formatRangeValue = (value: any) => {
                                if (value === null || value === undefined || value === "") return "";
                                const numValue = Number(value);
                                if (isNaN(numValue)) return value;
                                return numValue.toFixed(2);
                              };
                              
                              // Build age and gender info string
                              const buildAgeGenderInfo = () => {
                                const hasAgeMin = ageMin !== null && ageMin !== undefined && ageMin !== "";
                                const hasAgeMax = ageMax !== null && ageMax !== undefined && ageMax !== "";
                                
                                if (hasAgeMin || hasAgeMax) {
                                  const ageStr = hasAgeMin && hasAgeMax 
                                    ? `${ageMin} - ${ageMax}`
                                    : hasAgeMin 
                                    ? `≥ ${ageMin}`
                                    : `≤ ${ageMax}`;
                                  
                                  const genderStr = gender ? ` (${gender})` : "";
                                  return ` (Age: ${ageStr}${genderStr})`;
                                } else if (gender) {
                                  return ` (${gender})`;
                                }
                                
                                return "";
                              };
                              
                              const ageGenderInfo = buildAgeGenderInfo();
                              
                              if (hasMin && hasMax) {
                                // Range format: "Category: min - max (Age: ageMin - ageMax (gender))"
                                const formattedMin = formatRangeValue(rangeMin);
                                const formattedMax = formatRangeValue(rangeMax);
                                return (
                                  <div key={rangeIndex} className="text-sm text-gray-700">
                                    {categoryName ? `${categoryName}: ` : ""}{formattedMin} - {formattedMax}{ageGenderInfo}
                                  </div>
                                );
                              } else if (hasMin) {
                                // Minimum only: "Category: => min (Age: ageMin - ageMax (gender))"
                                const formattedMin = formatRangeValue(rangeMin);
                                return (
                                  <div key={rangeIndex} className="text-sm text-gray-700">
                                    {categoryName ? `${categoryName}: ` : ""}≥ {formattedMin}{ageGenderInfo}
                                  </div>
                                );
                              } else if (hasMax) {
                                // Maximum only: "Category: < max (Age: ageMin - ageMax (gender))"
                                const formattedMax = formatRangeValue(rangeMax);
                                return (
                                  <div key={rangeIndex} className="text-sm text-gray-700">
                                    {categoryName ? `${categoryName}: ` : ""}≤ {formattedMax}{ageGenderInfo}
                                  </div>
                                );
                              }
                              return null;
                            });
                          } else {
                            // Fallback to old format for backward compatibility
                            const refText = param.reference_text || param.referenceText;
                            const refMin = param.reference_min || param.referenceMin;
                            const refMax = param.reference_max || param.referenceMax;
                            
                            // Check if both refMin and refMax are not available
                            const hasMin = refMin !== null && refMin !== undefined && refMin !== "";
                            const hasMax = refMax !== null && refMax !== undefined && refMax !== "";
                            
                            if (!hasMin && !hasMax) {
                              // If both are not available, show Absent
                              return <div className="text-sm text-gray-700">Absent</div>;
                            }
                            
                            if (refText) {
                              return <div className="text-sm text-gray-700">{refText}</div>;
                            } else if (hasMin && hasMax) {
                              return <div className="text-sm text-gray-700">{refMin} - {refMax}</div>;
                            } else if (hasMin) {
                              return <div className="text-sm text-gray-700">=&gt; {refMin}</div>;
                            } else if (hasMax) {
                              return <div className="text-sm text-gray-700">&lt; {refMax}</div>;
                            }
                            return <div className="text-sm text-gray-700">Absent</div>;
                          }
                        };

                        return (
                          <tr
                            key={param.id || `${section.id}-${paramIndex}`}
                            className="hover:bg-gray-50"
                          >
                            <td className="px-4 py-3 text-sm text-gray-900 border-r border-gray-300 font-medium capitalize">
                              {param.name || "-"}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900 border-r border-gray-300">
                              -
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700 border-r border-gray-300">
                              {param.unit || "-"}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700 align-top">
                              <div className="space-y-0.5">
                                {formatReferenceValue()}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-8 text-center text-gray-500">
            No sections found for this test
          </div>
        )}
      </div>
      {test.description && (
        <div className="p-4 bg-gray-50 border border-gray-200">
          {/* <h3 className="text-md font-semibold text-gray-900 mb-2">
            Test Description
          </h3> */}

          {isDraftDescription(test.description) ? (
            <Editor
              editorState={getEditorStateFromRaw(test.description)}
              readOnly
              onChange={() => { }}
            />
          ) : (
            <p
              className="text-sm text-gray-700"
              style={{ whiteSpace: "pre-line" }}
            >
              {test.description}
            </p>
          )}
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex items-center justify-end gap-3 pt-6 mt-6 border-t border-gray-200">
        <button
          onClick={() => navigate("/test-list")}
          type="button"
          className="px-6 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
        >
          Back
        </button>
      </div>
    </div>
  );
};

export default ViewLabTest;
